/**
 * Fold a caption background color and optional opacity into a single CSS color.
 *
 * Opacity is only applied when the color is a parseable hex string (what the
 * editor's color picker produces). When opacity is unset or fully opaque the
 * original color is returned untouched, so captions authored before opacity
 * existed render exactly as before. Non-hex colors are passed through as-is
 * rather than dropped, so an unexpected value still shows a background.
 */
export function resolveBackgroundColor(
  color: string | undefined,
  opacity: number | undefined,
): string | undefined {
  if (!color) return undefined;
  if (opacity === undefined || opacity >= 1) return color;
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  const clamped = Math.max(0, Math.min(1, opacity));
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clamped})`;
}

/** Parse a 3/4/6/8-digit hex color into its RGB channels (alpha ignored). */
export function hexToRgb(
  hex: string,
): { r: number; g: number; b: number } | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(
    hex.trim(),
  );
  if (!match) return null;
  let h = match[1]!;
  if (h.length === 3 || h.length === 4) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return {
    r: Number(`0x${h.slice(0, 2)}`),
    g: Number(`0x${h.slice(2, 4)}`),
    b: Number(`0x${h.slice(4, 6)}`),
  };
}
